/**
 * Every message sent about one record, and Create New to send another
 * (WPS-175 requirement 3; any record since slice 11).
 *
 * The platform logs every send against its record, and the per-record read
 * surface (`records/<type>/<uuid>/activity/`) is what this card renders: a
 * reader asking "did the homeowner get the letter?" is answered here. Create
 * New opens the composer on the kinds this record sends (served per record
 * type) that this user may send (one permission per kind, D16).
 *
 * Tenant-agnostic. Every label, status and channel comes from the backend row;
 * nothing here knows which templates or channels a tenant uses.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Eye,
  FileText,
  Loader2,
  Lock,
  Mail,
  Mailbox,
  Plus,
  Upload,
} from "lucide-react";

import {
  ActivityChannelIcon,
  ActivityChannelLabel,
  ActivityChannelPill,
  ActivitySkipReason,
  ActivityStatusPill,
} from "@/admin/components/communications/activity-pills";
import { ActivityDetailPanel } from "@/admin/components/communications/activity-detail-panel";
import { RecordComposerDialog } from "@/admin/components/composer/record-composer-dialog";
import { DetailCard } from "@/admin/components/shared/detail-card";
import {
  useRecordCommunicationsActivity,
  type ActivityRow,
} from "@/admin/hooks/use-communications-activity";
import { useComposerRecordTypes, type ComposeKind } from "@/admin/hooks/use-composer";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { SEND_PERMISSION_BY_KIND } from "@/admin/lib/permissions";
import { useFormatters } from "@/shared/lib/formatting";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";

/** Enough to answer "what has this record sent" without an endless scroll. */
const PAGE_SIZE = 12;

/** What "Create New" can send. Each kind opens the composer on its templates. */
const COMPOSE_OPTIONS: {
  kind: ComposeKind;
  icon: typeof Mail;
  labelKey: string;
  label: string;
  hintKey: string;
  hint: string;
}[] = [
  {
    kind: "letter",
    icon: FileText,
    labelKey: "claimComms.create.letter",
    label: "Letter",
    hintKey: "claimComms.create.letterHint",
    hint: "A formal letter, kept on the claim as a PDF",
  },
  {
    kind: "evidence_request",
    icon: Upload,
    labelKey: "claimComms.create.request",
    label: "Document request",
    hintKey: "claimComms.create.requestHint",
    hint: "Ask for documents or photos, with an upload link",
  },
  {
    kind: "notification",
    icon: Mail,
    labelKey: "claimComms.create.email",
    label: "Email",
    hintKey: "claimComms.create.emailHint",
    hint: "Send one of your email templates",
  },
];

/**
 * One way to start a message from the card, as the Create new menu lists it.
 * A record's own card adds options (a claim's forms) the same way.
 */
export interface CreateOption {
  key: string;
  icon: typeof Mail;
  /** What it sends, and when to use it. */
  label: string;
  hint: string;
  testId: string;
  onSelect: () => void;
}

/** One entry in the Create new menu: what it sends, and when to use it. */
function CreateMenuItem({ option, onPick }: { option: CreateOption; onPick: () => void }) {
  const Icon = option.icon;
  return (
    <Button
      variant="ghost"
      data-testid={option.testId}
      onClick={onPick}
      className="flex h-auto w-full items-start justify-start gap-3 rounded-[8px] px-2.5 py-2 text-left font-light whitespace-normal hover:bg-[var(--bg-subtle)]"
    >
      <span className="mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-[var(--green-bg-muted)] text-[var(--green)]">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[13px] text-[var(--ink)]">{option.label}</span>
        <span className="text-[11px] leading-[1.4] text-[var(--ink-3)]">{option.hint}</span>
      </span>
    </Button>
  );
}

export function RecordCommunicationsCard({
  recordType,
  recordUuid,
  testIdPrefix,
  emptyLabel,
  letterHint,
  canAttachFiles = false,
  extraCreateOptions = [],
  children,
  folded,
  onToggleFold,
}: {
  /** The record type the composer reads, e.g. `claim`. */
  recordType: string;
  recordUuid: string;
  /** Prefix for the card's test ids, e.g. `claim-comms`. */
  testIdPrefix: string;
  /** What the card says when nothing has been sent yet. */
  emptyLabel: string;
  /** How Create New describes a letter here, when it differs. */
  letterHint?: string;
  /** A letter can carry the record's files, or ones uploaded for the send. */
  canAttachFiles?: boolean;
  /** More ways to start a message from this record, e.g. a claim's forms. */
  extraCreateOptions?: CreateOption[];
  /** Shown under the list, e.g. the record's forms. */
  children?: ReactNode;
  folded?: boolean;
  onToggleFold?: () => void;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const [openRow, setOpenRow] = useState<ActivityRow | null>(null);
  const [composeKind, setComposeKind] = useState<ComposeKind | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { can } = usePermissions();
  const { data: recordTypes = [] } = useComposerRecordTypes();

  const { data, isPending, isError, refetch } = useRecordCommunicationsActivity(
    recordType,
    recordUuid,
    { page_size: PAGE_SIZE },
  );
  const allRows = data?.results ?? [];

  // WPS-146: internal and external communications, the same split the Notes
  // card carries - "what has the homeowner been told?" and "who inside the
  // business has been pulled in?" are two questions. The audience is the
  // backend's call (membership, not email domain); nothing here re-derives it.
  const [audienceFilter, setAudienceFilter] = useState<
    "all" | "internal" | "external"
  >("all");
  const internalCount = allRows.filter((r) => r.audience === "internal").length;
  const externalCount = allRows.length - internalCount;
  const rows =
    audienceFilter === "all"
      ? allRows
      : allRows.filter((r) => r.audience === audienceFilter);

  // The kinds this record sends, each taking its own permission (D16), so
  // Create New offers only what this user may send from here.
  const kinds = recordTypes.find((type) => type.value === recordType)?.kinds ?? [];
  const composable = COMPOSE_OPTIONS.filter(
    (option) => kinds.includes(option.kind) && can(SEND_PERMISSION_BY_KIND[option.kind]),
  );
  const options: CreateOption[] = [
    ...composable.map((option) => ({
      key: option.kind,
      icon: option.icon,
      label: t(option.labelKey, option.label),
      hint:
        option.kind === "letter" && letterHint
          ? letterHint
          : t(option.hintKey, option.hint),
      testId: `${testIdPrefix}-create-${option.kind}`,
      onSelect: () => setComposeKind(option.kind),
    })),
    ...extraCreateOptions,
  ];

  return (
    <DetailCard
      title={t("claimComms.title", "Communications")}
      icon={<Mail className="h-3.5 w-3.5" />}
      collapsible={false}
      folded={folded}
      onToggleFold={onToggleFold}
      testId={`${testIdPrefix}-card`}
      trailing={
        options.length > 0 ? (
          // One button, one tidy menu that always drops down from it.
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger
              render={
                <Button
                  variant="outline"
                  size="xs"
                  data-testid={`${testIdPrefix}-create`}
                  className="h-7 rounded-full border-[var(--rule)] bg-[var(--bg-light)] px-3 text-[11px] font-normal text-[var(--ink-2)] hover:border-[var(--green)] hover:text-[var(--green)]"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t("claimComms.newLetter", "Create New")}
                  <ChevronDown className="ml-1 h-3 w-3 text-[var(--ink-3)]" />
                </Button>
              }
            />
            <PopoverContent
              side="bottom"
              align="end"
              keepSide
              className="w-[300px] p-1.5"
            >
              {options.map((option) => (
                <CreateMenuItem
                  key={option.key}
                  option={option}
                  onPick={() => {
                    setMenuOpen(false);
                    option.onSelect();
                  }}
                />
              ))}
            </PopoverContent>
          </Popover>
        ) : undefined
      }
    >
      {isPending ? (
        <div className="flex items-center gap-2 px-4 py-6 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t("claimComms.loading", "Loading communications...")}
        </div>
      ) : isError ? (
        <div className="flex items-center gap-3 px-4 py-6 text-[12px] text-[var(--ink-2)]">
          {t("claimComms.error", "Couldn't load communications.")}
          <Button variant="outline" size="xs" onClick={() => refetch()}>
            {t("common.retry", "Retry")}
          </Button>
        </div>
      ) : allRows.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">{emptyLabel}</p>
      ) : (
        <>
          {/* Only when there is a split to make: a record whose every message
              went one way needs no filter. */}
          {internalCount > 0 && externalCount > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pb-1 pt-3">
              <AudiencePill
                label={t("claimComms.filter.all", "All ({{count}})", {
                  count: allRows.length,
                })}
                active={audienceFilter === "all"}
                onClick={() => setAudienceFilter("all")}
              />
              <AudiencePill
                label={t("claimComms.filter.internal", "Internal ({{count}})", {
                  count: internalCount,
                })}
                active={audienceFilter === "internal"}
                onClick={() =>
                  setAudienceFilter(audienceFilter === "internal" ? "all" : "internal")
                }
              />
              <AudiencePill
                label={t("claimComms.filter.external", "External ({{count}})", {
                  count: externalCount,
                })}
                active={audienceFilter === "external"}
                onClick={() =>
                  setAudienceFilter(audienceFilter === "external" ? "all" : "external")
                }
              />
            </div>
          )}
          <div className="max-h-[420px] divide-y-[0.5px] divide-[var(--rule)] overflow-y-auto">
            {rows.map((row) => (
              <Button
                key={`${row.source}-${row.uuid}`}
                variant="ghost"
                onClick={() => setOpenRow(row)}
                className="flex h-auto w-full items-start gap-3 rounded-none px-4 py-3.5 text-left font-light whitespace-normal hover:bg-[var(--bg-subtle)]"
              >
                {/* The channel leads the row as an icon, so the eye finds the
                    subject next and the list reads as one column of messages. */}
                <ActivityChannelIcon channel={row.channel} className="mt-0.5" />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex w-full items-start gap-3">
                    {/* The backend writes the subject into `summary`; it is the
                        only thing a reader scans for, so it takes the width. */}
                    <span className="min-w-0 flex-1 truncate text-[13.5px] font-normal leading-[1.45] text-[var(--ink)]">
                      {row.summary || t("claimComms.noSubject", "No subject")}
                    </span>
                    <ActivityStatusPill status={row.status} className="shrink-0" />
                  </span>
                  <span className="flex w-full items-center gap-1.5 text-[11.5px] text-[var(--ink-3)]">
                    <span className="shrink-0 text-[var(--ink-2)]">
                      <ActivityChannelLabel channel={row.channel} />
                    </span>
                    <MetaDot />
                    <AudienceBadge audience={row.audience} />
                    <MetaDot />
                    <span className="min-w-0 flex-1 truncate">
                      {row.counterparty_name || row.counterparty}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatDateTime(row.occurred_at)}
                    </span>
                  </span>
                  {/* A message that never sent is the one a reader most needs
                      explaining, so the reason sits on the row, not behind a click. */}
                  {row.skip_reason_label && (
                    <ActivitySkipReason label={row.skip_reason_label} className="self-start" />
                  )}
                  {/* A posted letter says where it is in the mailroom's day:
                      waiting for the batch, sent to the mailroom, or retrying. */}
                  {row.mail_status && (
                    <span
                      className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[6px] bg-[var(--bg-subtle)] px-2 py-1 text-[11px] text-[var(--ink-2)]"
                      data-testid="comms-mail-status"
                    >
                      <Mailbox className="h-3 w-3 shrink-0 text-[var(--channel-mail)]" />
                      <span className="min-w-0">{row.mail_status}</span>
                    </span>
                  )}
                </span>
              </Button>
            ))}
          </div>
        </>
      )}

      {children}

      <RecordComposerDialog
        open={composeKind !== null}
        onOpenChange={(open) => !open && setComposeKind(null)}
        recordType={recordType}
        recordUuid={recordUuid}
        kind={composeKind ?? "letter"}
        canAttachFiles={canAttachFiles}
      />

      <Dialog open={!!openRow} onOpenChange={(open) => !open && setOpenRow(null)}>
        <DialogContent className="max-w-[1040px] sm:max-w-[1040px]">
          <DialogHeader>
            {/* The subject is what identifies a message to a reader, so it is
                the title. */}
            <DialogTitle className="pr-8">
              {openRow?.summary || t("claimComms.detailTitle", "Message")}
            </DialogTitle>
            {openRow && (
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pt-1.5">
                <ActivityChannelPill channel={openRow.channel} />
                <ActivityStatusPill status={openRow.status} />
                <span className="text-[12px] text-[var(--ink-2)]">
                  {openRow.counterparty_name || openRow.counterparty}
                </span>
                <span className="text-[12px] text-[var(--ink-3)]">
                  {formatDateTime(openRow.occurred_at)}
                </span>
              </div>
            )}
          </DialogHeader>
          {openRow && <ActivityDetailPanel row={openRow} />}
        </DialogContent>
      </Dialog>
    </DetailCard>
  );
}

// ---------------------------------------------------------------------------

/**
 * Who could read this message. Mirrors the Notes card's own indicator - same
 * two words, same lock/eye pair - so a reader learns the idea once per page.
 */
function AudienceBadge({ audience }: { audience: "internal" | "external" }) {
  const { t } = useTranslation();
  if (audience === "internal") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-[var(--ink-3)]">
        <Lock className="h-[11px] w-[11px]" />
        {t("claimComms.audience.internal", "Internal")}
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[var(--green)]">
      <Eye className="h-[11px] w-[11px]" />
      {t("claimComms.audience.external", "External")}
    </span>
  );
}

/** The quiet separator between facts on a row's meta line. */
function MetaDot() {
  return <span aria-hidden className="h-[3px] w-[3px] shrink-0 rounded-full bg-[var(--ink-3)] opacity-60" />;
}

function AudiencePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? "h-auto rounded-full border-[var(--green)] bg-[var(--green-bg)] px-2.5 py-0.5 text-[10px] font-normal text-[var(--ink)]"
          : "h-auto rounded-full border-[var(--rule)] bg-[var(--bg)] px-2.5 py-0.5 text-[10px] font-normal text-[var(--ink-3)]"
      }
    >
      {label}
    </Button>
  );
}
